/**
 * Camada de dados dos usuários do painel — delega na API Laravel `/admin/users`.
 */

import {
  isAbsoluteAdminUsername,
  type PainelMockUser,
  type PainelRole,
  type PainelUserStatus,
} from '@/lib/painel-rbac';
import type { SiteStaffRole } from '@/lib/cms-types';
import type { XatOfficialRoleLabel } from '@/lib/xat-official-roles';
import * as usersService from '@/services/users.service';

export type PainelUserEditableFields = {
  name: string;
  email: string;
  role: PainelRole;
  status: PainelUserStatus;
  officialXatRole?: XatOfficialRoleLabel | null;
  helpCenterRole?: SiteStaffRole | null;
  password?: string;
};

export type PainelUserCreateFields = PainelUserEditableFields & {
  username: string;
  password: string;
};

export async function getPainelUsers(): Promise<PainelMockUser[]> {
  return usersService.getUsers();
}

export async function savePainelUserPatch(
  id: string,
  patch: PainelUserEditableFields,
  options?: { targetUsername?: string; actorUsername?: string }
): Promise<PainelMockUser> {
  if (
    isAbsoluteAdminUsername(options?.targetUsername) &&
    !isAbsoluteAdminUsername(options?.actorUsername)
  ) {
    throw new Error('A conta Admin absoluta não pode ser editada por outros usuários.');
  }

  return usersService.updateUser(id, {
    name: patch.name,
    email: patch.email,
    role: patch.role,
    status: patch.status,
    official_xat_role: patch.officialXatRole ?? null,
    help_center_role: patch.helpCenterRole ?? null,
    ...(patch.password ? { password: patch.password } : {}),
  });
}

export async function createPainelUser(input: PainelUserCreateFields): Promise<PainelMockUser> {
  if (isAbsoluteAdminUsername(input.username)) {
    throw new Error('O username "admin" é reservado à conta admin absoluta do painel.');
  }

  return usersService.createUser({
    username: input.username,
    name: input.name,
    email: input.email,
    password: input.password,
    role: input.role,
    status: input.status,
    official_xat_role: input.officialXatRole ?? null,
    help_center_role: input.helpCenterRole ?? null,
  });
}

export async function deletePainelUser(
  id: string,
  options?: { targetUsername?: string }
): Promise<void> {
  if (isAbsoluteAdminUsername(options?.targetUsername)) {
    throw new Error('A conta Admin absoluta do painel não pode ser excluída.');
  }
  await usersService.deleteUser(id);
}
